import React from 'react';
import { AppProvider, useAppContext } from '@/contexts/AppContext';
import { Button } from '@/components/common/Button';
import { Alert } from '@/components/common/Alert';
import CompanyDeposit from '@/components/features/deposit/CompanyDeposit';
import PersonalDeposit from '@/components/features/deposit/PersonalDeposit';
import CollectParcel from '@/components/features/collect/CollectParcel';
import { STEPS } from '@/constants';
import { UserTypeEnum } from '@/types';
import { useTranslation } from 'react-i18next';
import { Link, Route, Routes, BrowserRouter } from 'react-router-dom';
import SimulatorPage from '@/components/features/simulator/SimulatorPage';
import ShipmentDropoff from './components/features/deposit/ShipmentDropoff';

const MainContent: React.FC = () => {
  const { step, setStep, userType, setUserType, error, setError, language, setLanguage } = useAppContext();
  const { t } = useTranslation();

  const handleDeposit = () => setStep(STEPS.DEPOSIT_TYPE_SELECTION);
  const handleCollect = () => setStep(STEPS.COLLECT_PHONE_ENTRY);
  
  const handleUserTypeSelect = (type: UserTypeEnum) => {
    setUserType(type);
    setStep(type === UserTypeEnum.Company ? STEPS.COMPANY_SELECTION : STEPS.PERSONAL_LOCKER_SELECTION);
  };

  const handleRestart = () => {
    setStep(STEPS.INITIAL);
    setUserType(null);
    setError('');
  };

  const handleDropoff = () => {
    console.log('handleDropoff');
    setStep(STEPS.DROPOFF_START);
  }

  const handleLanguageChange = (lang: string) => {
    setLanguage(lang);
  };

  const renderStep = () => {
    console.log(step);
    switch (step) {
      case STEPS.INITIAL:
        return (
          <>
            <h1 className="text-3xl font-bold text-center text-primary mb-6">{t('welcome')}</h1>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-center text-foreground mb-2">{t('selectLanguage')}</h2>
              <div className="flex justify-center space-x-4">
                <Button 
                  onClick={() => handleLanguageChange('sr')} 
                  variant={language === 'sr' ? 'default' : 'outline'}
                  size="lg"
                >
                  {t('serbian')}
                </Button>
                <Button 
                  onClick={() => handleLanguageChange('en')} 
                  variant={language === 'en' ? 'default' : 'outline'}
                  size="lg"
                >
                  {t('english')}
                </Button>
              </div>
            </div>
            <h2 className="text-xl font-bold text-center text-foreground mb-4">{t('selectAction')}</h2>
            <div className="space-y-4">
              <Button onClick={handleDeposit} variant="outline" size="lg" className="w-full">
                {t('deposit')}
              </Button>
              <Button onClick={handleCollect} variant="outline" size="lg" className="w-full">
                {t('collect')}
              </Button>
              <Button onClick={handleDropoff} variant="outline" size="lg" className="w-full">
                {t('dropoff')}
              </Button>
            </div>
          </>
        );
      case STEPS.DEPOSIT_TYPE_SELECTION:
        return (
          <>
            <h2 className="text-xl font-bold text-center text-foreground mb-4">{t('chooseDepositType')}</h2>
            <div className="space-y-4">
              <Button onClick={() => handleUserTypeSelect(UserTypeEnum.Company)} variant="outline" size="lg" className="w-full">
                {t('companyDeposit')}
              </Button>
              <Button onClick={() => handleUserTypeSelect(UserTypeEnum.Personal)} variant="outline" size="lg" className="w-full">
                {t('personalDeposit')}
              </Button>
              <Button onClick={handleRestart} variant="outline" size="lg" className="w-full">
                {t('cancel')}
              </Button>
            </div>
          </>
        );
      default:
        console.log("default");
        if (userType === UserTypeEnum.Company && step >= STEPS.COMPANY_SELECTION && step <= STEPS.DEPOSIT_COMPLETE) {
          return <CompanyDeposit />;
        }
        if (userType === UserTypeEnum.Personal && step >= STEPS.PERSONAL_LOCKER_SELECTION && step <= STEPS.DEPOSIT_COMPLETE) {
          return <PersonalDeposit />;
        }
        if (step >= STEPS.DROPOFF_START && step <= STEPS.DROPOFF_COMPLETE) {
          console.log("ShipmentDropoff");
          return <ShipmentDropoff />;
        }
        if (step >= STEPS.COLLECT_PHONE_ENTRY && step <= STEPS.COLLECT_COMPLETE || step === STEPS.COLLECT_PAYMENT  || 
          step === STEPS.COLLECT_PAYPAL || step === STEPS.COLLECT_TERMINAL) {
          return <CollectParcel />;
        }
        return null;
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-background p-4">
      <div className="w-full max-w-md bg-secondary rounded-lg shadow-lg p-6 space-y-6">
        {error && <Alert variant="destructive" className="mb-4">{error}</Alert>}
        <div className='text-center space-y-6'>
          {renderStep()}
        </div>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AppProvider>
        <Routes>
          <Route path="/simulator" element={<SimulatorPage />} />
          <Route path="/" element={<MainContent />} />
        </Routes>
      </AppProvider>
    </BrowserRouter>
  );
};

export default App;